// 账户与身份：英文字段。只维护显示文案，不改业务标识、数值或提交内容。

// 公共基础词条
export const base = Object.freeze({
  "account.quickEntry": "Quick access",
  "account.featuredTrade": "Featured trading",
  "account.userOverview": "Account and assets overview",
  "account.assetsOverview": "Assets overview",
  "account.defaultReviewLevel": "Review level 1",
  "account.avatar": "User avatar",
  "account.applyBroker": "Apply to become a negotiator",
  "account.loggedIn": "Signed in",
  "account.balance": "Account balance",
  "account.withdraw": "Withdraw",
  "account.withdrawable": "Available to withdraw",
  "account.frozen": "On hold",
  "account.points": "Points",
  "account.coins": "Coins",
  "account.tradeOrders": "Trade orders",
  "account.goodsCenter": "Product center",
  "account.messages": "Messages",
  "account.goodsLibrary": "Product library",
  "account.suppliers": "Supplier management",
  "account.logoutCurrent": "Sign out",
  "account.comingSoon": "Coming soon",
});

// 账户设置
export const settings = Object.freeze({
  "accountSettings.save": "Save",
  "accountSettings.reset": "Reset",
  "accountSettings.security.title": "Security Settings",
  "accountSettings.security.oldPassword": "Current password",
  "accountSettings.security.enterOldPassword": "Enter your current password",
  "accountSettings.security.newPassword": "New password",
  "accountSettings.security.enterNewPassword": "Enter a new password",
  "accountSettings.security.confirmPassword": "Confirm password",
  "accountSettings.security.enterConfirmPassword":
    "Enter the new password again",
  "accountSettings.security.mobileVerification": "Phone verification",
  "accountSettings.security.enterCode": "Enter the verification code",
  "accountSettings.security.resendAfter": "Resend in {seconds}s",
  "accountSettings.security.getCode": "Get code",
  "accountSettings.security.tipsTitle": "Security tips",
  "accountSettings.security.tipLength":
    "Use at least 8 characters, including letters and numbers",
  "accountSettings.security.tipComplexity":
    "Avoid passwords that are easy to guess",
  "accountSettings.security.tipRotation":
    "Change your password regularly to protect your account",
  "accountSettings.security.tipPublic":
    "Do not enter your password in public places",
  "accountSettings.security.passwordMismatch": "The passwords do not match",
  "accountSettings.security.minLength": "Use at least 8 characters",
  "accountSettings.security.codeSent": "Verification code sent",
  "accountSettings.security.passwordChanged": "Password updated",
  "accountSettings.personal.title": "Personal Information",
  "accountSettings.personal.avatar": "Profile photo",
  "accountSettings.personal.nickname": "Nickname",
  "accountSettings.personal.enterNickname": "Enter a nickname",
  "accountSettings.personal.mobile": "Phone number",
  "accountSettings.personal.enterMobile": "Enter a phone number",
  "accountSettings.personal.invalidMobile": "Enter a valid phone number",
  "accountSettings.personal.email": "Email",
  "accountSettings.personal.enterEmail": "Enter an email address",
  "accountSettings.personal.gender": "Gender",
  "accountSettings.personal.male": "Male",
  "accountSettings.personal.female": "Female",
  "accountSettings.personal.unspecified": "Prefer not to say",
  "accountSettings.personal.birthday": "Date of birth",
  "accountSettings.personal.selectDate": "Select a date",
  "accountSettings.personal.address": "Address",
  "accountSettings.personal.enterAddress": "Enter an address",
  "accountSettings.personal.avatarFormat":
    "Profile photos must be JPG or PNG files!",
  "accountSettings.personal.avatarSize": "Profile photos must be under 2 MB!",
  "accountSettings.personal.avatarUploaded": "Profile photo uploaded",
  "accountSettings.personal.saved": "Saved successfully",
  "accountSettings.verification.title": "Account Verification",
  "accountSettings.verification.realNameTitle": "Identity Verification",
  "accountSettings.verification.verified": "Verified",
  "accountSettings.verification.unverified": "Not verified",
  "accountSettings.verification.realName": "Legal name",
  "accountSettings.verification.enterRealName": "Enter your legal name",
  "accountSettings.verification.idCard": "ID number",
  "accountSettings.verification.enterIdCard": "Enter your ID number",
  "accountSettings.verification.invalidIdCard": "Enter a valid ID number",
  "accountSettings.verification.idCardFront": "Front of ID card",
  "accountSettings.verification.uploadIdCardFront":
    "Upload the front of your ID card",
  "accountSettings.verification.idCardBack": "Back of ID card",
  "accountSettings.verification.uploadIdCardBack":
    "Upload the back of your ID card",
  "accountSettings.verification.submit": "Submit for verification",
  "accountSettings.verification.companyTitle": "Company Verification",
  "accountSettings.verification.companyName": "Company name",
  "accountSettings.verification.enterCompanyName": "Enter your company name",
  "accountSettings.verification.businessLicense": "Business license",
  "accountSettings.verification.uploadBusinessLicense":
    "Upload your business license",
  "accountSettings.verification.loading": "Loading verification status...",
  "accountSettings.verification.realNameSubmitted":
    "Identity verification submitted",
  "accountSettings.verification.companySubmitted":
    "Company verification submitted",
});

// 登录注册
export const auth = Object.freeze({
  "auth.supportedSmsRegions":
    "SMS is available for mainland China and Hong Kong. Use email for other regions",
  "auth.invalidHongKongMobile":
    "Enter a valid Hong Kong mobile number (8 digits)",

  "auth.codeLoginMethods": "Verification code sign-in methods",
  "auth.emailLogin": "Email sign-in",
  "auth.emailCodeLogin": "Email code sign-in",
  "auth.codeLogin": "Code sign-in",
  "auth.switchCodeLogin": "Switch to code sign-in",
  "auth.returnCodeLogin": "Back to code sign-in",
  "auth.emailAddress": "Email",
  "auth.enterEmailAddress": "Enter your email",
  "auth.emailCode": "Email verification code",
  "auth.defaultMobileVerification": "Mobile verification by default",
  "auth.defaultEmailVerification": "Email verification by default",
  "auth.mainlandSmsUseEmail":
    "SMS currently supports mainland China mobile numbers only. Use email sign-in instead",
  "auth.verificationCodeSendFailed": "Failed to send the verification code",
  "auth.emailVerificationCodeSendFailed":
    "Failed to send the email verification code",
  "auth.emailVerificationCodeSent": "Email verification code sent",
  "auth.codeLoginPasswordResetHint":
    "Sign in with a verification code, then change your password in your account settings",
  "auth.emailCodeSendFailed":
    "Failed to send the email verification code. Try again later",
  "auth.emailCodeSent": "Email verification code sent",
  "auth.brandScene": "XSOU supply chain",
  "auth.brandHeadlineFirst": "Connect supply with demand",
  "auth.brandHeadlineSecond": "faster, smarter, and more reliably",
  "auth.brandService": "Excellent service",
  "auth.brandCreation": "Continuous innovation",
  "auth.brandCollaboration": "Shared success",
  "auth.brandValue": "Shared value",
  "auth.close": "Close",
  "auth.accountActions": "Account actions",
  "auth.accountType": "Account type",
  "auth.login": "Log in",
  "auth.register": "Sign up",
  "auth.mobile": "Mobile number",
  "auth.mobileOrEmail": "Mobile number / Email",
  "auth.areaCode": "Country or region code",
  "auth.registerAreaCode": "Registration country or region code",
  "auth.areaMainland": "Mainland China +86",
  "auth.areaHongKong": "Hong Kong, China +852",
  "auth.areaMacau": "Macao, China +853",
  "auth.areaTaiwan": "Taiwan, China +886",
  "auth.areaNorthAmerica": "US / Canada +1",
  "auth.areaSingapore": "Singapore +65",
  "auth.enterMobile": "Enter your mobile number",
  "auth.smsCode": "SMS verification code",
  "auth.enterCode": "Enter the code",
  "auth.resendAfter": "Resend in {seconds}s",
  "auth.getCode": "Get code",
  "auth.loggingIn": "Logging in…",
  "auth.noAccount": "New to XSOU?",
  "auth.registerNow": "Sign up now",
  "auth.passwordLogin": "Password login",
  "auth.switchPasswordLogin": "Switch to password login",
  "auth.wechatLogin": "WeChat login",
  "auth.switchWechatLogin": "Switch to WeChat login",
  "auth.password": "Password",
  "auth.forgotPassword": "Forgot password?",
  "auth.enterMobileOrEmail": "Enter your mobile number or email",
  "auth.enterLoginPassword": "Enter your password",
  "auth.hidePassword": "Hide password",
  "auth.showPassword": "Show password",
  "auth.smsLogin": "SMS login",
  "auth.switchSmsLogin": "Switch to SMS login",
  "auth.wechatLoginQr": "WeChat login QR code",
  "auth.wechatRegisterQr": "WeChat registration QR code",
  "auth.qrLoading": "Loading QR code",
  "auth.qrExpired": "QR code expired",
  "auth.refreshQr": "Refresh",
  "auth.scanWechatToLogin": "Scan with WeChat to log in",
  "auth.scanWechatLoginHint": "Open WeChat and scan the code to confirm login",
  "auth.returnSmsLogin": "Back to SMS login",
  "auth.personalAccount": "Personal account",
  "auth.companyAccount": "Business account",
  "auth.mainland": "Mainland China",
  "auth.overseas": "Hong Kong, Macao, Taiwan and overseas",
  "auth.defaultMobileRegistration": "Register with a mobile number",
  "auth.verifyByMobile": "Verify with a mobile number",
  "auth.defaultEmailRegistration": "Register with email",
  "auth.verifyByEmail": "Verify with email",
  "auth.scanWechatToRegister": "Scan with WeChat to sign up",
  "auth.scanWechatRegisterHint":
    "Open WeChat and scan the code to sign up and log in",
  "auth.returnMobileRegistration": "Back to mobile registration",
  "auth.returnEmailRegistration": "Back to email registration",
  "auth.mainlandMobile": "Mainland China mobile number",
  "auth.overseasMobile": "Overseas mobile number",
  "auth.email": "Email address",
  "auth.enterEmail": "Enter your email address",
  "auth.code": "Verification code",
  "auth.enterFourDigitCode": "Enter the 4-digit code",
  "auth.loginPassword": "Login password",
  "auth.enterPasswordLength": "Enter an 8–20 character password",
  "auth.companyName": "Business name",
  "auth.enterCompanyName": "Enter the full business name",
  "auth.contactName": "Contact name",
  "auth.enterContactName": "Enter the contact name",
  "auth.registering": "Signing up…",
  "auth.finishRegistration": "Complete registration",
  "auth.haveAccount": "Already have an account?",
  "auth.loginNow": "Log in now",
  "auth.wechatRegistration": "Sign up with WeChat",
  "auth.registrationSuccess": "Registration successful",
  "auth.companyAccountCreated": "Business account created",
  "auth.personalAccountCreated": "Personal account created",
  "auth.registrationCompleted": "Registration complete: {context}.",
  "auth.sentenceSeparator": " ",
  "auth.companyCertificationHint":
    "Verify your business to access all features.",
  "auth.enterXsouHint": "You can now use XSOU.",
  "auth.continueCompanyCertification": "Verify your business",
  "auth.skipAndEnter": "Skip for now and enter XSOU",
  "auth.enterXsou": "Enter XSOU",
  "auth.agreementPrefix": "I have read and agree to the",
  "auth.userAgreement": "User Agreement",
  "auth.agreementConjunction": "and",
  "auth.privacyPolicy": "Privacy Policy",
  "auth.emailRegistration": "Email registration",
  "auth.mobileRegistration": "Mobile registration",
  "auth.agreementRequired":
    "Please read and agree to the User Agreement and Privacy Policy before logging in or signing up. Confirming will select the checkbox for you.",
  "auth.loginAgreementRequired":
    "Please read and agree to the User Agreement and Privacy Policy before logging in. Confirm to agree and continue.",
  "auth.agreementTitle": "User Agreement and Privacy Policy",
  "auth.confirm": "Confirm",
  "auth.cancel": "Cancel",
  "auth.agreeAndLogin": "Agree and log in",
  "auth.internationalSmsUnavailable": "International SMS is not available yet",
  "auth.invalidMainlandMobile": "Enter a valid mainland China mobile number",
  "auth.smsSent": "SMS code sent",
  "auth.internationalLoginUnavailable":
    "International mobile login is not available yet",
  "auth.invalidMobile": "Enter a valid mobile number",
  "auth.invalidFourDigitCode": "Enter a valid 4-digit code",
  "auth.accountAndPasswordRequired": "Enter your account and password",
  "auth.forgotPasswordUnavailable": "Password recovery is not available yet",
  "auth.qrFetchFailed": "Could not load the QR code",
  "auth.wechatRegistrationSuccess": "WeChat registration successful",
  "auth.invalidEmail": "Enter a valid email address",
  "auth.emailCodeUnavailable": "Email verification is not available yet",
  "auth.invalidOverseasMobile": "Enter a mobile number with 6–20 digits",
  "auth.invalidPasswordLength": "Enter an 8–20 character login password",
  "auth.companyNameRequired": "Enter the business name",
  "auth.contactNameRequired": "Enter the contact name",
  "auth.registrationExpired":
    "Registration details expired. Please sign up again",
  "auth.registrationWelcome": "Registration successful. Welcome to XSOU",
  "auth.loginSuccess": "Logged in successfully",
  "auth.requestFailed": "Request failed. Please try again later",
});

// 移动登录
export const mobileLogin = Object.freeze({
  "mobileLogin.heroBackground": "Login page background",
  "mobileLogin.back": "Back",
  "mobileLogin.loginHint": "Login introduction",
  "mobileLogin.welcome": "Welcome to XSOU",
  "mobileLogin.heroLineOne": "Connect real supply and demand,",
  "mobileLogin.heroLineTwo": "and trade components more efficiently",
  "mobileLogin.accountAction": "Account actions",
  "mobileLogin.login": "Log in",
  "mobileLogin.register": "Sign up",
  "mobileLogin.passwordLogin": "Password login",
  "mobileLogin.smsLogin": "SMS login",
  "mobileLogin.accountPlaceholder": "Enter mobile number or email",
  "mobileLogin.passwordPlaceholder": "Enter your password",
  "mobileLogin.forgotPassword": "Forgot password?",
  "mobileLogin.loggingIn": "Logging in…",
  "mobileLogin.mobilePlaceholder": "Enter mobile number",
  "mobileLogin.codePlaceholder": "Enter verification code",
  "mobileLogin.resendCountdown": "Resend in {seconds}s",
  "mobileLogin.getCode": "Get code",
  "mobileLogin.noAccount": "Don't have an account?",
  "mobileLogin.registerNow": "Sign up now",
  "mobileLogin.otherLoginMethods": "Other login methods",
  "mobileLogin.wechatLogin": "Log in with WeChat",
  "mobileLogin.registerType": "Registration type",
  "mobileLogin.personalAccount": "Personal account",
  "mobileLogin.companyAccount": "Business account",
  "mobileLogin.registerRegion": "Registration region",
  "mobileLogin.mainland": "Mainland China",
  "mobileLogin.defaultMobileRegister": "Mobile registration by default",
  "mobileLogin.mobileVerification": "Verify by mobile number",
  "mobileLogin.overseas": "Hong Kong, Macao, Taiwan & overseas",
  "mobileLogin.defaultEmailRegister": "Email registration by default",
  "mobileLogin.emailVerification": "Verify by email",
  "mobileLogin.mainlandMobile": "Mainland China mobile number",
  "mobileLogin.overseasMobile": "Overseas mobile number",
  "mobileLogin.commonEmail": "Email address",
  "mobileLogin.mainlandCode": "Mainland China +86",
  "mobileLogin.hongKongCode": "Hong Kong, China +852",
  "mobileLogin.macaoCode": "Macao, China +853",
  "mobileLogin.taiwanCode": "Taiwan, China +886",
  "mobileLogin.usCanadaCode": "US/Canada +1",
  "mobileLogin.singaporeCode": "Singapore +65",
  "mobileLogin.registrationAreaCode": "Registration country or region code",
  "mobileLogin.areaCode": "Country or region code",
  "mobileLogin.emailPlaceholder": "Enter your email address",
  "mobileLogin.verificationCode": "Verification code",
  "mobileLogin.fourDigitCodePlaceholder": "Enter 4-digit code",
  "mobileLogin.loginPassword": "Password",
  "mobileLogin.newPasswordPlaceholder": "Enter an 8–20 character password",
  "mobileLogin.companyName": "Company name",
  "mobileLogin.companyNamePlaceholder": "Enter full company name",
  "mobileLogin.contactName": "Contact name",
  "mobileLogin.contactNamePlaceholder": "Enter contact name",
  "mobileLogin.submitting": "Submitting…",
  "mobileLogin.completeRegistration": "Complete registration",
  "mobileLogin.haveAccount": "Already have an account?",
  "mobileLogin.loginNow": "Log in now",
  "mobileLogin.otherRegisterMethods": "Other registration methods",
  "mobileLogin.emailRegister": "Sign up with email",
  "mobileLogin.mobileRegister": "Sign up with mobile number",
  "mobileLogin.wechatRegister": "Sign up with WeChat",
  "mobileLogin.readAndAgree": "I have read and agree to",
  "mobileLogin.userAgreement": "the User Agreement",
  "mobileLogin.and": "and",
  "mobileLogin.privacyPolicy": "the Privacy Policy",
  "mobileLogin.internationalSmsUnavailable":
    "International SMS is not available yet",
  "mobileLogin.invalidMainlandMobile":
    "Enter a valid mainland China mobile number",
  "mobileLogin.smsCodeSent": "SMS verification code sent",
  "mobileLogin.internationalLoginUnavailable":
    "International mobile login is not available yet",
  "mobileLogin.invalidMobile": "Enter a valid mobile number",
  "mobileLogin.invalidSmsCode": "Enter a valid SMS verification code",
  "mobileLogin.accountPasswordRequired": "Enter your account and password",
  "mobileLogin.invalidEmail": "Enter a valid email address",
  "mobileLogin.emailCodeUnavailable": "Email verification is not available yet",
  "mobileLogin.invalidOverseasMobile": "Enter a 6–20 digit mobile number",
  "mobileLogin.invalidFourDigitCode": "Enter a 4-digit verification code",
  "mobileLogin.invalidPasswordLength": "Enter an 8–20 character password",
  "mobileLogin.companyNameRequired": "Enter your company name",
  "mobileLogin.contactNameRequired": "Enter a contact name",
  "mobileLogin.registrationSucceeded": "Registration successful",
  "mobileLogin.requestError": "Request error",
  "mobileLogin.loginSucceeded": "Login successful",
  "mobileLogin.forgotPasswordHint":
    "Log in with SMS first, then change your password in My Account",
  "mobileLogin.agreementRequired":
    "Please read and agree to the User Agreement and Privacy Policy",
  "mobileLogin.agreementTitle": "User Agreement and Privacy Policy",
  "mobileLogin.agreementMessage":
    "Please read and agree to the User Agreement and Privacy Policy before logging in. Confirming will check the box and continue.",
  "mobileLogin.agreeAndLogin": "Agree and log in",
  "mobileLogin.cancel": "Cancel",
  "mobileLogin.authorizationMissing":
    "Authorization details are missing. Please try again",
  "mobileLogin.requestFailedRetry": "Request failed. Please try again later",
});

// 移动实名认证
export const mobileRealname = Object.freeze({
  "realname.title": "Mainland China ID Verification",
  "realname.securityTitle": "Your information is protected",
  "realname.securityDescription":
    "Your details are used only to verify your identity. We protect your privacy.",
  "realname.documents": "Mainland China ID card",
  "realname.idFront": "ID card front (portrait side)",
  "realname.uploadIdFront": "Upload ID card front",
  "realname.idBack": "ID card back (national emblem side)",
  "realname.uploadIdBack": "Upload ID card back",
  "realname.basicInformation": "Basic information",
  "realname.matchDocumentHint":
    "Enter the details exactly as shown on your ID card",
  "realname.name": "Full name",
  "realname.namePlaceholder": "Enter the name on your ID card",
  "realname.nameInvalid":
    "Name is required, cannot contain digits, and must be 100 characters or fewer",
  "realname.idNumber": "ID card number",
  "realname.idNumberPlaceholder": "Enter your 18-digit ID card number",
  "realname.idNumberInvalid": "Enter a valid ID card number",
  "realname.mobile": "Mainland China mobile number",
  "realname.mobilePlaceholder": "Enter your Mainland China mobile number",
  "realname.mobileRequired": "Mobile number is required",
  "realname.smsCode": "SMS code",
  "realname.smsCodePlaceholder": "Enter the code",
  "realname.smsCodeRequired": "SMS code is required",
  "realname.useMobileAbove": "The mobile number above will be used",
  "realname.wechatPlaceholder": "Enter your WeChat ID",
  "realname.wechat": "WeChat ID",
  "realname.sameAsMobile": "Same as mobile number",
  "realname.submitting": "Submitting...",
  "realname.submit": "Submit verification",
  "realname.getCode": "Get code",
  "realname.resendAfter": "Resend in {seconds}s",
  "realname.wechatRequired":
    "Enter a WeChat ID or select Same as mobile number",
  "realname.mobileInvalid": "Enter a valid mobile number",
  "realname.smsSent": "SMS sent. Check your messages",
  "realname.smsFailed": "Could not send the code. Try again later",
  "realname.completeRequired": "Complete all required fields",
  "realname.smsCodeInvalid": "Enter a valid SMS code",
  "realname.success": "Identity verified!",
  "realname.submitFailed": "Submission failed",
  "realname.submitRetry": "Submission failed. Try again later",
});

// 移动钱包
export const mobileWallet = Object.freeze({
  "wallet.title": "My Wallet",
  "wallet.loading": "Loading wallet data",
  "wallet.accountAssets": "Account assets",
  "wallet.accountBalance": "Account balance",
  "wallet.goToWithdraw": "Go to withdrawals",
  "wallet.withdraw": "Withdraw",
  "wallet.viewDetails": "View details",
  "wallet.pointsDetails": "Points history",
  "wallet.pointsDescription": "Wiki review rewards and points transactions",
  "wallet.recordCount": "{count} records",
  "wallet.pointsLoading": "Loading points history",
  "wallet.loadingShort": "Loading",
  "wallet.pointsChange": "Points change",
  "wallet.balanceAfter": "Balance {balance}",
  "wallet.noPointsRecords": "No points records",
  "wallet.rewardDetails": "Reward history",
  "wallet.rewardDescription":
    "Successful trade rewards and withdrawal progress",
  "wallet.transactionReward": "Successful trade reward",
  "wallet.identityUnknown": "Role not specified",
  "wallet.loadFailed": "Could not load wallet data",
  "wallet.noRewardRecords": "No reward records",
  "wallet.reload": "Reload",
  "wallet.withdrawableBalance": "Available to withdraw",
  "wallet.frozenBalance": "On hold",
  "wallet.points": "Points",
  "wallet.coins": "Coins",
  "wallet.partialLoadFailed":
    "Some wallet data could not load. Try again later",
  "wallet.pending": "Pending",
  "wallet.received": "Received",
  "wallet.processing": "Processing",
});

// 企业资料
export const company = Object.freeze({
  "companyInfo.companyInformation": "Company Information",
  "companyInfo.shippingInformation": "Shipping Information",
  "companyInfo.contacts": "Contacts",
  "companyInfo.subsidiaries": "Subsidiaries",
  "companyInfo.addAddressAction": "+ Add Address",
  "companyInfo.addContactAction": "+ Add Contact",
  "companyInfo.businessLicense": "Business License",
  "companyInfo.region": "Region",
  "companyInfo.mainlandChina": "Mainland China",
  "companyInfo.hongKongMacaoTaiwan": "Hong Kong, Macao & Taiwan",
  "companyInfo.uploadBusinessLicense": "Upload Business License",
  "companyInfo.issuingAuthority": "Issuing Authority",
  "companyInfo.enterText": "Enter text",
  "companyInfo.validityPeriod": "Validity Period",
  "companyInfo.selectStartDate": "Select Start Date",
  "companyInfo.selectEndDate": "Select End Date",
  "companyInfo.basicInformation": "Basic Information",
  "companyInfo.companyNameChinese": "Company Name (Chinese)",
  "companyInfo.companyNameEnglish": "Company Name (English)",
  "companyInfo.shortName": "Short Name",
  "companyInfo.taxId": "Tax ID",
  "companyInfo.supplierCode": "Supplier Code",
  "companyInfo.linkedCompany": "Linked Company",
  "companyInfo.setAsParentCompany": "Set as Parent Company",
  "companyInfo.setAsSubsidiary": "Set as Subsidiary",
  "companyInfo.personInChargeLegalRepresentative":
    "Person in Charge / Legal Representative",
  "companyInfo.companyPhone": "Company Phone",
  "companyInfo.companyAddress": "Company Address",
  "companyInfo.companyLogo": "Company Logo",
  "companyInfo.recommendedImageSize120120Px":
    "Recommended image size: 120 × 120 px",
  "companyInfo.billingInformation": "Billing Information",
  "companyInfo.invoiceDetails": "Invoice Details",
  "companyInfo.uploadStampedBankAndBillingDetails":
    "Upload stamped bank and billing details",
  "companyInfo.uploadInvoiceDetails": "Upload Invoice Details",
  "companyInfo.bankAccounts": "Bank Accounts",
  "companyInfo.bankAccount": "Bank Account",
  "companyInfo.addAction": "+ Add",
  "companyInfo.delete": "Delete",
  "companyInfo.bankName": "Bank Name",
  "companyInfo.bankBranch": "Bank Branch",
  "companyInfo.bankShortName": "Bank Short Name",
  "companyInfo.setAsDefaultBank": "Set as Default Bank",
  "companyInfo.defaultBank": "Default Bank",
  "companyInfo.bankRoutingNumber": "Bank Routing Number",
  "companyInfo.paymentDetailsAttachment": "Payment Details Attachment",
  "companyInfo.uploadAttachment": "Upload Attachment",
  "companyInfo.additionalInformation": "Additional Information",
  "companyInfo.companyType": "Company Type",
  "companyInfo.limitedLiabilityCompany": "Limited Liability Company",
  "companyInfo.jointStockCompany": "Joint Stock Company",
  "companyInfo.soleProprietorship": "Sole Proprietorship",
  "companyInfo.partnership": "Partnership",
  "companyInfo.productCategories": "Product Categories",
  "companyInfo.productCategory1": "Product Category 1",
  "companyInfo.productCategory2": "Product Category 2",
  "companyInfo.brand1": "Brand 1",
  "companyInfo.brand2": "Brand 2",
  "companyInfo.scope1": "Scope 1",
  "companyInfo.scope2": "Scope 2",
  "companyInfo.scope3": "Scope 3",
  "companyInfo.category1": "Category 1",
  "companyInfo.category2": "Category 2",
  "companyInfo.category3": "Category 3",
  "companyInfo.issuingAuthorityName": "Issuing authority name",
  "companyInfo.consumerElectronics": "Consumer Electronics",
  "companyInfo.industrialControl": "Industrial Control",
  "companyInfo.automotiveElectronics": "Automotive Electronics",
  "companyInfo.energyControl": "Energy Control",
  "companyInfo.smartSecurity": "Smart Security",
  "companyInfo.applications": "Applications",
  "companyInfo.applicationIndustryExamples":
    "For example: consumer electronics, industrial control, automotive electronics, LED, energy control, smart security, and home appliances",
  "companyInfo.brands": "Brands",
  "companyInfo.businessScope": "Business Scope",
  "companyInfo.enterYourMainBusinessAreasThenPressEnterToAddEachOne":
    "Enter your main business areas, then press Enter to add each one",
  "companyInfo.add": "Add",
  "companyInfo.mainProducts": "Main Products",
  "companyInfo.enterYourMainProductsThenPressEnterToAddEachOne":
    "Enter your main products, then press Enter to add each one",
  "companyInfo.companyIntroduction": "Company Introduction",
  "companyInfo.certificates": "Certificates",
  "companyInfo.honorsAndQualifications": "Honors and Qualifications",
  "companyInfo.uploadOwnBrandAgencyBrandAndQualificationCertificates":
    "Upload own-brand, agency-brand, and qualification certificates",
  "companyInfo.reset": "Reset",
  "companyInfo.save": "Save",
  "companyInfo.shippingAddress": "Shipping Address",
  "companyInfo.default": "Default",
  "companyInfo.edit": "Edit",
  "companyInfo.shippingContacts": "Shipping Contacts",
  "companyInfo.setAsDefault": "Set as Default",
  "companyInfo.chineseName": "Chinese Name",
  "companyInfo.englishName": "English Name",
  "companyInfo.mobileNumber": "Mobile Number",
  "companyInfo.wechat": "WeChat",
  "companyInfo.landline": "Landline",
  "companyInfo.more": "More",
  "companyInfo.actions": "Actions",
  "companyInfo.companyName": "Company Name",
  "companyInfo.legalRepresentative": "Legal Representative",
  "companyInfo.location": "Location",
  "companyInfo.subsidiary": "Subsidiary",
  "companyInfo.switch": "Change",
  "companyInfo.details": "Details",
  "companyInfo.companyDetails": "Company Details",
  "companyInfo.editCompanyDetails": "Edit Company Details",
  "companyInfo.file": "File",
  "companyInfo.setAsDefaultShippingAddress": "Set as Default Shipping Address",
  "companyInfo.uploadABusinessLicenseToRecognizeItsText":
    "Upload a business license to recognize its text",
  "companyInfo.viewAttachment": "View Attachment",
  "companyInfo.paymentDetails": "Payment Details",
  "companyInfo.interbankRoutingNumber": "Bank Routing Number",
  "companyInfo.uploadAgain": "Upload Again",
  "companyInfo.recognizeImage": "Recognize Image",
  "companyInfo.pasteAndRecognize": "Paste and Recognize",
  "companyInfo.shippingAddressChinese": "Shipping Address (Chinese)",
  "companyInfo.shippingAddressEnglish": "Shipping Address (English)",
  "companyInfo.recipient": "Recipient",
  "companyInfo.recipientPhone": "Recipient Phone",
  "companyInfo.contactChinese": "Contact (Chinese)",
  "companyInfo.contactEnglish": "Contact (English)",
  "companyInfo.ms": "Ms.",
  "companyInfo.mr": "Mr.",
  "companyInfo.addButton": "+ Add",
  "companyInfo.weChatNumber": "WeChat ID",
  "companyInfo.linkCompany": "+ Link Company",
  "companyInfo.contactsHometown": "Contact's Hometown",
  "companyInfo.personality": "Personality",
  "companyInfo.interests": "Interests",
  "companyInfo.linkedNegotiator": "Linked Negotiator",
  "companyInfo.setAsDefaultContact": "Set as Default Contact",
  "companyInfo.saveContact": "Save Contact",
  "companyInfo.addAddress": "Add Address",
  "companyInfo.editAddress": "Edit Address",
  "companyInfo.addContact": "Add Contact",
  "companyInfo.addEditContact": "Add / Edit Contact",
  "companyInfo.enterTheCompanyNameInChinese":
    "Enter the company name in Chinese",
  "companyInfo.enterTheCompanyNameInEnglish":
    "Enter the company name in English",
  "companyInfo.enterAShortCompanyName": "Enter a short company name",
  "companyInfo.enterTheTaxId": "Enter the tax ID",
  "companyInfo.enterTheSupplierCode": "Enter the supplier code",
  "companyInfo.searchForACompanyToLink": "Search for a company to link",
  "companyInfo.enterAName": "Enter a name",
  "companyInfo.enterTheCompanyPhone": "Enter the company phone",
  "companyInfo.enterTheCompanyAddress": "Enter the company address",
  "companyInfo.uploadCompanyLogo": "Upload Company Logo",
  "companyInfo.enterTheBankName": "Enter the bank name",
  "companyInfo.enterTheBankBranch": "Enter the bank branch",
  "companyInfo.enterTheBankCode": "Enter the bank code",
  "companyInfo.enterAShortBankName": "Enter a short bank name",
  "companyInfo.enterTheBankAccount": "Enter the bank account",
  "companyInfo.enterTheBankRoutingNumber": "Enter the bank routing number",
  "companyInfo.enterTheSwiftCode": "Enter the SWIFT code",
  "companyInfo.select": "Select",
  "companyInfo.enterAnApplication": "Enter an application",
  "companyInfo.uploadImage": "Upload Image",
  "companyInfo.enterABrandName": "Enter a brand name",
  "companyInfo.describeYourCompanyInUpTo500Characters":
    "Describe your company in up to 500 characters",
  "companyInfo.certificateTitle": "Certificate Title",
  "companyInfo.uploadFile": "Upload File",
  "companyInfo.noShippingAddresses": "No shipping addresses",
  "companyInfo.noContacts": "No contacts",
  "companyInfo.searchAddressRecipientOrPhone":
    "Search address, recipient, or phone",
  "companyInfo.noSubsidiaries": "No subsidiaries",
  "companyInfo.pasteCompanyDetailsHereForAutomaticRecognitionAndFilling":
    "Paste company details here for automatic recognition and filling",
  "companyInfo.enterTheShippingAddressInChinese":
    "Enter the shipping address in Chinese",
  "companyInfo.enterTheShippingAddressInEnglish":
    "Enter the shipping address in English",
  "companyInfo.enterTheRecipient": "Enter the recipient",
  "companyInfo.enterTheRecipientPhone": "Enter the recipient phone",
  "companyInfo.enterTheContactNameInChinese":
    "Enter the contact name in Chinese",
  "companyInfo.enterTheContactNameInEnglish":
    "Enter the contact name in English",
  "companyInfo.enterAMobileNumber": "Enter a mobile number",
  "companyInfo.enterAWechatId": "Enter a WeChat ID",
  "companyInfo.enterALandlineNumber": "Enter a landline number",
  "companyInfo.enterTheLinkedCompany": "Enter the linked company",
  "companyInfo.enterTheContactsHometown": "Enter the contact's hometown",
  "companyInfo.searchANegotiatorByPhone": "Search a negotiator by phone",
  "companyInfo.bankPaymentDetailsUploaded": "Bank payment details uploaded",
  "companyInfo.savedSuccessfully": "Saved successfully",
  "companyInfo.couldNotSave": "Could not save",
  "companyInfo.defaultContactUpdated": "Default contact updated",
  "companyInfo.deleteThisContact": "Delete this contact?",
  "companyInfo.deleteThisAddress": "Delete this address?",
  "companyInfo.deleteThisCompany": "Delete this company?",
  "companyInfo.deleteThisBankAccount": "Delete this bank account?",
  "companyInfo.confirm": "Confirm",
  "companyInfo.deletedSuccessfully": "Deleted successfully",
  "companyInfo.youCanAddMoreMobileNumbers": "You can add more mobile numbers",
  "companyInfo.youCanAddMoreWechatIds": "You can add more WeChat IDs",
  "companyInfo.linkACompany": "Link a Company",
  "companyInfo.cannotSaveContactCompanyIdIsMissing":
    "Cannot save contact: company ID is missing",
  "companyInfo.defaultShippingAddressUpdated":
    "Default shipping address updated",
  "companyInfo.selectAnImageFile": "Select an image file",
  "companyInfo.selectAnImageOrEnterTextFirst":
    "Select an image or enter text first",
  "companyInfo.informationRecognized": "Information recognized",
  "companyInfo.recognitionFailed": "Recognition failed",
  "companyInfo.recognitionFailedPleaseTryAgain":
    "Recognition failed. Please try again.",
  "companyInfo.cannotSaveAddressCompanyIdIsMissing":
    "Cannot save address: company ID is missing",
  "companyInfo.batchManagement": "Batch Management",
  "companyInfo.newCompany": "New Company",
  "companyInfo.switchLinkedCompany": "Switch Linked Company",
  "companyInfo.editBankAccount": "Edit Bank Account",
  "companyInfo.addBankAccount": "Add Bank Account",
});

// 个人中心菜单
export const menu = Object.freeze({
  "personalMenu.outboundManagement": "Shipment management",
  "personalMenu.contractTranslation": "Contract translation review",
  "personalMenu.contentTranslation": "Content translations",
  "personalMenu.featuredTrading": "Featured trading",
  "personalMenu.wallet": "My wallet",
  "personalMenu.reportDashboard": "Data dashboard",
  "personalMenu.orders": "Transaction orders",
  "personalMenu.goodsCenter": "Product center",
  "personalMenu.goodsLibrary": "Product library",
  "personalMenu.brands": "Brand management",
  "personalMenu.suppliers": "Supplier management",
  "personalMenu.messages": "Messages",
  "personalMenu.supplyDemand": "Supply & demand",
  "personalMenu.postSupply": "Post supply",
  "personalMenu.postDemand": "Post demand",
  "personalMenu.quickSupply": "Quick supply post",
  "personalMenu.quickDemand": "Quick demand post",
  "personalMenu.bulkPost": "Bulk post",
  "personalMenu.customerManagement": "Customer management",
  "personalMenu.myCustomers": "My customers",
  "personalMenu.myOrders": "My orders",
  "personalMenu.memberLevel": "Membership levels",
  "personalMenu.levelList": "Level list",
  "personalMenu.levelApplication": "Level applications",
  "personalMenu.levelBenefits": "Level benefits",
  "personalMenu.memberIdentity": "Member identities",
  "personalMenu.identityList": "Identity list",
  "personalMenu.salesperson": "Salesperson",
  "personalMenu.customer": "Customer",
  "personalMenu.supplier": "Supplier",
  "personalMenu.partner": "Partner",
  "personalMenu.bidManagement": "Bidding management",
  "personalMenu.bidReview": "Bid review",
  "personalMenu.bidList": "Bid list",
  "personalMenu.postBid": "Post a bid",
  "personalMenu.myBids": "My bids",
  "personalMenu.myFollowers": "My order followers",
  "personalMenu.myFollowUps": "My follow-ups",
  "personalMenu.favorites": "My favorites",
  "personalMenu.wiki": "Chip wiki",
  "personalMenu.reviewManagement": "Review management",
  "personalMenu.myPosts": "My posts",
  "personalMenu.myQuestions": "My Q&A",
  "personalMenu.specifications": "Datasheet management",
  "personalMenu.accountSettings": "Account settings",
  "personalMenu.personalInfo": "Personal information",
  "personalMenu.companyInfo": "Company information",
  "personalMenu.security": "Security settings",
  "personalMenu.verification": "Account verification",
  "personalMenu.priceChannel": "Price channel",
  "personalMenu.channelIdentity": "Channel identity",
  "personalMenu.messageCenter": "Message center",
  "personalMenu.watchlists": "Market watchlists",
  "personalMenu.certification": "Company verification",
  "personalMenu.submissions": "Business submissions",
  "personalMenu.reviewQueue": "Review queue",
  "personalMenu.reviewResults": "Review results",
  "personalMenu.loading": "Loading...",
  "personalMenu.loadFailed": "Couldn't load data. Please try again.",
  "personalMenu.loginFirst": "Please sign in first",
});

// 桌面实名认证
export const desktopRealname = Object.freeze({
  "pcRealname.title": "Identity verification",
  "pcRealname.back": "Back",
  "pcRealname.personalVerification": "Personal verification",
  "pcRealname.submitIdentity": "Submit identity details",
  "pcRealname.identityInstructions":
    "Upload your valid ID document and ensure the details match it.",
  "pcRealname.pending": "Not verified",
  "pcRealname.documentMaterials": "ID documents",
  "pcRealname.fileRequirements": "JPG or PNG, up to 5 MB per image",
  "pcRealname.bothSidesRequired": "Upload both sides",
  "pcRealname.portraitSide": "ID portrait side",
  "pcRealname.portraitDescription": "The side showing your name and ID number",
  "pcRealname.uploadPortrait": "Upload portrait side",
  "pcRealname.emblemSide": "ID emblem side",
  "pcRealname.emblemDescription":
    "The side showing the issuing authority and validity period",
  "pcRealname.uploadEmblem": "Upload emblem side",
  "pcRealname.basicInfo": "Basic information",
  "pcRealname.requiredHint":
    "Fields marked * are required. Check your details before submitting.",
  "pcRealname.fullName": "Full name",
  "pcRealname.namePlaceholder": "Enter the name on your ID",
  "pcRealname.idNumber": "ID number",
  "pcRealname.idPlaceholder": "Enter your 18-digit ID number",
  "pcRealname.mobile": "Mobile number",
  "pcRealname.mobilePlaceholder": "Enter your mobile number",
  "pcRealname.smsCode": "SMS verification code",
  "pcRealname.smsPlaceholder": "Enter the code",
  "pcRealname.getCode": "Get code",
  "pcRealname.resendIn": "Resend in {count}s",
  "pcRealname.wechatId": "WeChat ID",
  "pcRealname.wechatPlaceholder": "Enter your usual WeChat ID",
  "pcRealname.sameMobile": "Same as mobile",
  "pcRealname.invalidName":
    "Name is required, cannot contain digits and must be at most 100 characters",
  "pcRealname.invalidId": "Enter a valid ID number",
  "pcRealname.mobileRequired": "Enter your mobile number",
  "pcRealname.smsRequired": "Enter the SMS verification code",
  "pcRealname.privacyNotice":
    "Your information is used only for identity verification and is protected by the platform.",
  "pcRealname.cancel": "Cancel",
  "pcRealname.submit": "Submit verification",
  "pcRealname.process": "Verification process",
  "pcRealname.estimatedTime": "About 1–2 minutes",
  "pcRealname.uploadDocuments": "Upload documents",
  "pcRealname.uploadDocumentsHint": "Submit both sides of your ID",
  "pcRealname.completeInformation": "Complete your details",
  "pcRealname.completeInformationHint":
    "Provide your identity and contact details",
  "pcRealname.finishVerification": "Finish verification",
  "pcRealname.finishVerificationHint":
    "Submit to complete identity verification",
  "pcRealname.photoRequirements": "Photo requirements",
  "pcRealname.photoCorners":
    "Show all four corners; do not crop or cover the ID",
  "pcRealname.photoClarity": "Keep text clear and avoid glare or shadows",
  "pcRealname.photoValidity": "Upload your own ID while it is still valid",
  "pcRealname.invalidMobile": "Enter a valid mobile number",
  "pcRealname.smsSent": "SMS sent. Please check your messages.",
  "pcRealname.smsSendFailed": "Could not send the SMS. Please try again later.",
  "pcRealname.completeRequired": "Complete all required fields",
  "pcRealname.wechatOrMobileRequired":
    "Enter your WeChat ID or select Same as mobile",
  "pcRealname.invalidSms": "Enter a valid SMS verification code",
  "pcRealname.success": "Identity verified!",
  "pcRealname.submitFailed": "Submission failed",
});

// 桌面钱包
export const desktopWallet = Object.freeze({
  "pcWallet.title": "My wallet",
  "pcWallet.subtitle": "View account assets and transaction rewards",
  "pcWallet.overview": "Account overview",
  "pcWallet.overviewHint": "Funds and account benefits",
  "pcWallet.balance": "Account balance",
  "pcWallet.assets": "Account assets",
  "pcWallet.totalBalance": "Current total balance",
  "pcWallet.withdraw": "Withdraw",
  "pcWallet.withdrawable": "Available to withdraw",
  "pcWallet.withdrawableHint": "Eligible for withdrawal",
  "pcWallet.frozen": "Frozen amount",
  "pcWallet.frozenHint": "Pending settlement",
  "pcWallet.points": "Points",
  "pcWallet.pointsHint": "View points history",
  "pcWallet.coins": "Coins",
  "pcWallet.benefits": "Account benefits",
  "pcWallet.pointsLedger": "Points history",
  "pcWallet.pointsLedgerHint": "Review wiki rewards and points activity",
  "pcWallet.noPoints": "No points history",
  "pcWallet.changeDescription": "Description",
  "pcWallet.pointsChange": "Points change",
  "pcWallet.pointsBalance": "Points balance",
  "pcWallet.changedAt": "Changed at",
  "pcWallet.rewards": "Rewards",
  "pcWallet.rewardsHint": "Completed-trade rewards and withdrawal progress",
  "pcWallet.name": "Name",
  "pcWallet.tradeReward": "Completed-trade reward",
  "pcWallet.tradeIdentity": "Trading role",
  "pcWallet.amount": "Amount",
  "pcWallet.withdrawalProgress": "Withdrawal status",
  "pcWallet.pending": "Pending",
  "pcWallet.received": "Received",
});

// 手机端业务流程：固定界面文案。
export const mobileWorkflow = Object.freeze({
  "evaluation.result": "Review results for this transaction",
  "evaluation.instructions":
    "Rate your actual experience. Reviews cannot be changed after submission.",
  "evaluation.awaitingRating": "Awaiting rating",
  "evaluation.rateTarget": "Overall rating for {target}",
  "supplier.fileCount": "{count} files",
  "profile.myServices": "My services",
  "profile.myReviews": "My reviews",
  "profile.inProgress": "In progress",
  "profile.frequentFirst": "Frequent term 1",
  "profile.frequentSecond": "Frequent term 2",
  "account.confirmLogout": "Sign out of this account?",
  "evaluation.requestFailed": "Request failed",
  "evaluation.succeeded": "Review submitted",
  "evaluation.failed": "Unable to submit the review",
  "supplier.taxNumber": "Supplier tax number",
  "evaluation.description": "Review instructions",
  "evaluation.overall": "Overall rating",
  "evaluation.negative": "Negative",
  "evaluation.neutral": "Neutral",
  "evaluation.positive": "Positive",
  "evaluation.categoryRatings": "Category ratings",
  "evaluation.attitude": "Service attitude",
  "evaluation.attitudeScore": "Service attitude rating",
  "evaluation.service": "Service quality",
  "evaluation.serviceScore": "Service quality rating",
  "evaluation.response": "Response speed",
  "evaluation.responseScore": "Response speed rating",
  "evaluation.noRecipients": "No service providers available to review",
  "evaluation.submit": "Submit review",
  "evaluation.recipientCount": "review recipients",
  "profile.sampleYears": "Joined x years ago",
  "profile.companyPlaceholder": "｜Company name",
  "profile.overallScore": "Overall score",
  "profile.sampleOrders": "26 orders",
  "profile.completedDeals": "Completed deals",
  "profile.positiveScore": "Positive review score",
});

// PC界面显示文案；不改动业务原值。
export const desktopWalletFollowup = Object.freeze({
  "pcWallet.noWithdrawals": "No withdrawal history",
  "pcWallet.totalCredit": "Total credit: {amount}",
});

export const companyDialogs = Object.freeze({
  "companyDialogs.setDefaultBankFailed": "Unable to set the default bank",
  "companyDialogs.addressIntro":
    "Enter or select the buyer's delivery address for this purchase order",
  "companyDialogs.recipientNameRequired": "Enter the recipient's name",
  "companyDialogs.noAddresses": "No alternative addresses",
  "companyDialogs.dropImage": "Drop an image file here",
  "companyDialogs.bankShortNameRequired": "Enter the bank's short name",
  "companyDialogs.accountInformation": "Account information",
  "companyDialogs.accountNameRequired": "Enter the account name",
  "companyDialogs.accountAndCodes": "Account and bank codes",
  "companyDialogs.defaultBankHint":
    "The selected bank will also become the default bank",
  "companyDialogs.switchBankHint":
    "Select a different bank and confirm to update the bank details",
  "companyDialogs.noBanks": "No banks available",
  "companyDialogs.accountPrefix": "Account: ",
  "companyDialogs.switchBank": "Change bank",
  "companyDialogs.noAlternativeBanks": "No alternative banks",
  "companyDialogs.confirmBankSwitch": "Confirm change",
  "companyDialogs.companyNameZh": "Company name (Chinese)",
  "companyDialogs.companyNameEn": "Company name (English)",
  "companyDialogs.bankNameZh": "Bank name (Chinese)",
  "companyDialogs.bankNameEn": "Bank name (English)",
  "companyDialogs.companySearchFailed":
    "Company search failed. Please try again",
  "companyDialogs.companySearchPlaceholder": "Search by company name",
  "companyDialogs.companyCreateFailed": "Unable to create the company",
  "companyDialogs.bankSwitched": "Bank details updated",
  "companyDialogs.createdBankRefresh":
    "The bank was created. Refresh the company details and select it again",
  "companyDialogs.confirmDeleteSupplier": "Permanently delete “{name}”?",
  "companyDialogs.contactCreated": "Contact created",
  "companyDialogs.contactChanged": "Contact updated",
  "companyDialogs.defaultBankSet": "Default bank updated",
  "companyDialogs.bankConfirmed": "Bank details confirmed",
  "companyDialogs.addressCreated": "Address added",
  "companyDialogs.addressChanged": "Address updated",
  "companyDialogs.confirmChangeAddress": "Confirm change",
  "companyDialogs.newMainlandBank": "New mainland bank",
  "companyDialogs.newHongKongBank": "New Hong Kong bank",
  "companyDialogs.mainlandBankIntro":
    "Enter mainland bank account details for purchase order settlement",
  "companyDialogs.hongKongBankIntro":
    "Enter Hong Kong bank account details for purchase order settlement",
  "companyDialogs.bankEntityMissing":
    "The bank record has no ID and cannot be set as default",
  "companyDialogs.unnamedBank": "Unnamed bank",
});

export const sharedFlowUi = Object.freeze({
  "companyDialogs.parentCompanyRequired":
    "Select a parent company when creating a subsidiary",
  "companyDialogs.yearFiles": "{year} ({count})",
  "companyDialogs.yearOption": "{year}",
  "companyDialogs.monthOption": "{month}",
  "companyDialogs.dayOption": "{day}",
  "googleFeedback.unavailableLabel": "{label} unavailable: {error}",
  "googleFeedback.configurationUnavailable":
    "Google sign-in configuration is unavailable",
  "googleFeedback.initializationFailed": "Unable to initialize Google sign-in",
  "googleFeedback.unavailableRetry":
    "Google sign-in is unavailable. Please try again",
  "googleFeedback.verifyAccount": "Verify your existing account",
  "googleFeedback.reconnect": "Google sign-in failed. Please reconnect",
});

// 钱包接口返回的固定来源与系统流水描述；不改写历史账本。
export const walletLedger = Object.freeze({
  "walletLedger.firstDatasheetDownload": "First datasheet download",
  "walletLedger.repeatDatasheetDownload": "Repeat datasheet download",
  "walletLedger.datasheetDownload": "Datasheet download",
  "walletLedger.datasheetDownloadCharge": "Points charged for datasheet download",
  "walletLedger.wikiReviewReward": "Wiki review reward",
  "walletLedger.wikiReviewApprovedReward": "Reward for approved wiki review",
  "walletLedger.wikiPublishReward": "Wiki publication reward",
  "walletLedger.wikiContentPublished": "Wiki content approved and published",
  "walletLedger.wikiFavoriteReward": "Wiki favorite reward",
  "walletLedger.pointsTopUp": "Points top-up",
  "walletLedger.pointsRefund": "Points refund",
  "walletLedger.pointsAdjustment": "Points adjustment",
  "walletLedger.adminAdjustment": "Administrator points adjustment",
  "walletLedger.systemGift": "System credit",
  "walletLedger.withdrawDebit": "Withdrawal request deducted from available balance",
  "walletLedger.withdrawTotalIncrease": "Withdrawal request added to cumulative withdrawals",
  "walletLedger.withdrawRefund": "Rejected withdrawal returned to available balance",
  "walletLedger.withdrawTotalDecrease": "Rejected withdrawal deducted from cumulative withdrawals",
  "walletLedger.creditRepayment": "Credit repayment",
  "walletLedger.balanceCreditRepayment": "Balance used for credit repayment",
  "walletLedger.sellerReferrer": "Seller referrer",
  "walletLedger.sellerNegotiator": "Seller negotiator",
  "walletLedger.sellerBusiness": "Seller business representative",
  "walletLedger.buyerReferrer": "Buyer referrer",
  "walletLedger.buyerNegotiator": "Buyer negotiator",
  "walletLedger.buyerBusiness": "Buyer business representative",
  "walletLedger.supervisor": "Supervisor",
  "walletLedger.inspector": "Inspector",
  "walletLedger.noBalanceRecords": "No balance transactions",
  "walletLedger.withDetail": "{reason}: {detail}",
  "walletLedger.creditUpgrade": "Credit grade upgraded: L{before} -> L{after}",
});

export const messages = Object.freeze({
  ...walletLedger,
  ...sharedFlowUi,
  ...companyDialogs,
  ...desktopWalletFollowup,
  ...mobileWorkflow,
  ...base,
  ...settings,
  ...auth,
  ...mobileLogin,
  ...mobileRealname,
  ...mobileWallet,
  ...company,
  ...menu,
  ...desktopRealname,
  ...desktopWallet,
});
